import { fn } from '@ember/helper'
import { array } from '@ember/helper'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import RouteTemplate from 'ember-route-template'
import { eq, not } from 'ember-truth-helpers'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { SummaryTablesStudents } from 'teacher-ui-client/components/summary-tables/students'
import { SummaryTablesTeachers } from 'teacher-ui-client/components/summary-tables/teachers'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import isSubco from 'teacher-ui-client/helpers/is-subco'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import type RexSummaryTableRoute from 'teacher-ui-client/reporting/ui-scope/rex/summary/table/route'
import TabGroup from 'teacher-ui-client/components/tab-group'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import type Owner from '@ember/owner'

type SummaryTab = 'teachers' | 'students'

interface Signature {
  Args: {
    model: ModelFor<RexSummaryTableRoute>
  }
}

export class RexSummaryTablePage extends Component<Signature> {
  @tracked currentTab?: SummaryTab

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)

    this.currentTab = args.model.isSubco ? 'teachers' : 'students'
  }

  get isTeacherTab() {
    return this.currentTab === 'teachers'
  }

  setCurrentTab = (tab: SummaryTab) => {
    this.currentTab = tab
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.summaryReportTitles.heading"}} />
    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.CSV ExportType.print}}
      @isDataPresent={{true}}
      @showScopePicker={{not this.isTeacherTab}}
      @showDatePicker={{true}}
      @studentRouteName="reporting.ui-scope.rex.student-summary"
    />
    {{#if (isSubco)}}
      <TabGroup @theme={{themeKey "reading"}} @tabType={{TAB_TYPE_KEYS.shadow}} data-test-tab-group as |tabGroup|>
        <tabGroup.tab
          data-test-tab-all-teachers
          @isActive={{eq this.currentTab "teachers"}}
          {{on "click" (fn this.setCurrentTab "teachers")}}
        >
          {{t "reporting.summaryReportTitles.teachersTab"}}
        </tabGroup.tab>
        <tabGroup.tab
          data-test-tab-my-students
          @isActive={{eq this.currentTab "students"}}
          {{on "click" (fn this.setCurrentTab "students")}}
        >
          {{t "reporting.summaryReportTitles.studentsTab"}}
        </tabGroup.tab>
      </TabGroup>
    {{/if}}

    <ContentTile class="relative print:border-none {{if (isSubco) 'rounded-tl-none'}}">
      {{#if this.isTeacherTab}}
        <SummaryTablesTeachers
          @product={{@model.product}}
          @teachers={{@model.readingTeachers}}
          @teachersData={{@model.schoolSimpleSummary.teachers}}
          @schoolData={{@model.schoolSimpleSummary.school}}
        />
      {{else}}
        <SummaryTablesStudents
          @product={{@model.product}}
          @students={{@model.students}}
          @studentsData={{@model.studentsSimpleSummary.students}}
          @totalsData={{@model.studentsSimpleSummary.totals}}
        />
      {{/if}}
    </ContentTile>
  </template>
}

export default RouteTemplate(RexSummaryTablePage)
